import { Box, Typography } from "@mui/material"
import { useMmsService } from "services/mms/context/MmsServiceContext"
import { MmsServiceInterface } from "interfaces/mms-service"
import React, { useCallback, useEffect, useState } from "react"
import { InvoicesFilter, IFormInvoiceInput } from "layouts/invoices/filter"
import { SubmitHandler } from "react-hook-form"
import { MainPageContentContainer } from "components/MainPageContentContainer"
import { InvoiceInfo, ListInvoiceInfo } from "types/list-invoice"
import { Loader } from "components/Loader"
import { InvoiceTable } from "layouts/invoices/table/invoice-table.tsx"
import {
    filterInvoices,
    invoiceSort,
} from "layouts/invoices/utils/utils-invoices.ts"
import { InvoicesTableColumnsType } from "layouts/invoices/utils/types"
import { SORTORDER, SortOrder } from "types/sort"
import {
    defaultFilterValuesInvoices,
    invoicesColumns,
} from "layouts/invoices/utils/const"
import {ConnexionError} from "components/ConnexionError.tsx";

export const InvoicesPage = () => {
    const [filterValues, setFilterValues] = useState<IFormInvoiceInput | null>(
        null
    )
    const [noResultFilter, setNoResultFilter] = useState(false)

    const [sortOrder, setSortOrder] = useState<SortOrder>(SORTORDER.DESC)
    const [orderBy, setOrderBy] =
        useState<keyof InvoicesTableColumnsType>("date")
    const [filteredInvoices, setFilteredInvoices] = useState<InvoiceInfo[]>([])
    const [sortedInvoices, setSortedInvoices] = useState<InvoiceInfo[]>([])
    const [, setLoading] = useState(true)

    const mmsService: MmsServiceInterface = useMmsService()
    const invoiceService = mmsService.getInvoiceService()
    const {
        data: rawInvoices,
        success,
        isLoading,
        sendRequest: fetchListUsers,
    } = invoiceService.useGetListInvoices()
    const [listInvoices, setListInvoices] = useState<InvoiceInfo[]>([])

    useEffect(() => {
        fetchListUsers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => {
        if (rawInvoices !== null && success) {
            setListInvoices(rawInvoices as InvoiceInfo[])
            setLoading(false)
        }
    }, [rawInvoices, success])

    const onFilterSubmit: SubmitHandler<IFormInvoiceInput> = useCallback(
        (filterValuesForm) => {
            if (
                JSON.stringify(filterValuesForm) ===
                JSON.stringify(defaultFilterValuesInvoices)
            ) {
                setFilterValues(null)
            } else {
                setFilterValues(filterValuesForm)
            }
        },
        []
    )

    useEffect(() => {
        if (!filterValues) setFilteredInvoices(listInvoices)
        else {
            const filterFn = filterInvoices(filterValues)
            const filteredInvoicesResult = listInvoices.filter(filterFn)
            setFilteredInvoices(filteredInvoicesResult)
        }
    }, [listInvoices, filterValues])

    useEffect(() => {
        const { comparatorFn } = invoicesColumns[orderBy]
        const sortFn = invoiceSort(sortOrder, comparatorFn)
        const sortedInvoicesResult = [...filteredInvoices].sort(sortFn)
        setSortedInvoices(sortedInvoicesResult)
    }, [filteredInvoices, sortOrder, orderBy])

    useEffect(() => {
        if (filteredInvoices.length === 0 && filterValues) {
            setNoResultFilter(true)
        } else {
            setNoResultFilter(false)
        }
    }, [filteredInvoices, filterValues])

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
            }}
        >
            <InvoicesFilter onSubmit={onFilterSubmit} />
            <MainPageContentContainer sx={{
                p:"2.25rem",
            }}>
                <InvoicesContainer
                    apiSuccess={success}
                    rows={sortedInvoices}
                    loading={isLoading}
                    sortOrder={sortOrder}
                    orderBy={orderBy}
                    setSortOrder={setSortOrder}
                    setOrderBy={setOrderBy}
                    noResultFilter={noResultFilter}
                />
            </MainPageContentContainer>
        </Box>
    )
}

const InvoicesContainer = ({
    apiSuccess,
    rows,
    loading,
    sortOrder,
    orderBy,
    setSortOrder,
    setOrderBy,
    noResultFilter,
}: {
    apiSuccess: boolean | null
    rows: ListInvoiceInfo
    loading: boolean
    sortOrder: SortOrder
    orderBy: keyof InvoicesTableColumnsType
    setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>
    setOrderBy: React.Dispatch<
        React.SetStateAction<keyof InvoicesTableColumnsType>
    >
    noResultFilter: boolean
}) => {
    return loading || !apiSuccess ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
            {!apiSuccess && !loading ? <ConnexionError /> : <Loader />}
        </Box>
    ) : (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1.313rem",
            }}
        >
            <Typography variant="headlineLarge" sx={{ color: "grey.900" }}>
                Factures
            </Typography>
            {noResultFilter ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Typography sx={{ color: "grey.600" }}>
                        Aucun résultat
                    </Typography>
                </Box>
            ) : (
                <InvoiceTable
                    rows={rows}
                    sortOrder={sortOrder}
                    orderBy={orderBy}
                    setSortOrder={setSortOrder}
                    setOrderBy={setOrderBy}
                />
            )}
        </Box>
    )
}
